import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
/**
 * The declaration review's agent-addressable CONTROLS — the four gestures chat
 * deliberately does not perform, each resolved to the button a person presses.
 *
 * Not entries in the shared `agentTargets` catalog, and cannot be: that catalog
 * keys a target by a fixed route, and every one of these lives on ONE
 * declaration's review. The route is built per action (`declarationReviewPath`)
 * and the selector is attached to it here, which is the same split
 * `openInquiryDraft` already makes for the composer's Send button.
 *
 * Each control is marked in the DOM with `data-agent-target="<key>"`, the one
 * convention `targetSelector` reads.
 */
import {
  targetSelector,
  type AgentTarget,
} from "@/features/agent-chat/actions/agentTargets"

export const DECLARATION_REVIEW_CONTROLS = [
  "confirm_statement",
  "apply_scope",
  "verify_article",
  "finish_review",
] as const

export type DeclarationReviewControl =
  (typeof DECLARATION_REVIEW_CONTROLS)[number]

interface ControlDescriptor {
  key: string
  labelKey: TranslationKeys
}

const CONTROLS: Record<DeclarationReviewControl, ControlDescriptor> = {
  confirm_statement: {
    key: "declaration.confirm-statement",
    labelKey: "AI_CHAT/TARGET_LABEL_DECL_CONFIRM_STATEMENT",
  },
  apply_scope: {
    key: "declaration.apply-scope",
    labelKey: "AI_CHAT/TARGET_LABEL_DECL_APPLY_SCOPE",
  },
  verify_article: {
    key: "declaration.verify-article",
    labelKey: "AI_CHAT/TARGET_LABEL_DECL_VERIFY_ARTICLE",
  },
  finish_review: {
    key: "declaration.finish-review",
    labelKey: "AI_CHAT/TARGET_LABEL_DECL_FINISH_REVIEW",
  },
}

/**
 * Every review screen mounts its cards only once the declaration's own reads have
 * resolved, and a confirm control additionally waits for the statement it belongs
 * to — so none of these is in the DOM on arrival. The default wait expires first
 * and reports the control as missing when it was merely not there yet.
 */
const CONTROL_WAIT_MS = 10_000

export function isDeclarationReviewControl(
  value: unknown,
): value is DeclarationReviewControl {
  return (
    typeof value === "string" &&
    (DECLARATION_REVIEW_CONTROLS as readonly string[]).includes(value)
  )
}

/** The control to pulse once `route` has rendered. TUR-1835 part 1: only the
 *  studio footer's Approve carries its target so far (`declaration.finish-review`);
 *  the other three are not on the page yet, so no caller pulses any of them until
 *  the rest of that ticket marks them and wires `actionExecutor`. */
export function declarationReviewControlTarget(
  control: DeclarationReviewControl,
  route: string,
): AgentTarget {
  const descriptor = CONTROLS[control]
  return {
    route,
    selector: targetSelector(descriptor.key),
    labelKey: descriptor.labelKey,
    waitTimeoutMs: CONTROL_WAIT_MS,
  }
}
